import React from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import Icon from '../ui/Icon';
import { KpiCard, PageSkeleton, EmptyState } from '../ui/primitives';
import { API_URL } from '../config/api';

type Daily = { day: string; orders: number; packages: number };
type DeliveryType = { name: string; value: number; color: string };
type Hourly = { hour: string; time: number };

function BarChart({ data }: { data: Daily[] }) {
  const max = Math.max(...data.map((d) => Math.max(d.packages, d.orders)), 1);
  return (
    <div>
      <div className="chart-bar">
        {data.map((d) => (
          <div className="bcol" key={d.day}>
            <div className="bstack">
              <div className="bseg" style={{ height: `${(d.orders / max) * 100}%`, background: 'var(--accent)' }} title={`${d.orders} zam.`} />
              <div className="bseg" style={{ height: `${(Math.max(0, d.packages - d.orders) / max) * 100}%`, background: 'var(--accent-soft-2)', borderRadius: '0 0 4px 4px' }} title={`${d.packages} paczek`} />
            </div>
            <div className="blabel">{d.day}</div>
          </div>
        ))}
      </div>
      <div className="legend-row">
        <div className="li"><span className="sw" style={{ background: 'var(--accent)' }} /> Zamówienia</div>
        <div className="li"><span className="sw" style={{ background: 'var(--accent-soft-2)' }} /> Paczki</div>
      </div>
    </div>
  );
}

function Donut({ mix }: { mix: DeliveryType[] }) {
  const total = mix.reduce((s, m) => s + m.value, 0) || 1;
  let acc = 0;
  const R = 52; const C = 2 * Math.PI * R;
  return (
    <div className="donut-wrap">
      <svg width="130" height="130" viewBox="0 0 130 130">
        <circle cx="65" cy="65" r={R} fill="none" stroke="var(--surface-3)" strokeWidth="20" />
        {mix.map((m) => {
          const frac = m.value / total;
          const dash = frac * C;
          const el = (
            <circle key={m.name} cx="65" cy="65" r={R} fill="none" stroke={m.color || 'var(--accent)'} strokeWidth="20"
              strokeDasharray={`${dash} ${C - dash}`} strokeDashoffset={-acc * C} transform="rotate(-90 65 65)" />
          );
          acc += frac;
          return el;
        })}
        <text x="65" y="62" textAnchor="middle" style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, fill: 'var(--fg-1)' }}>{total}%</text>
        <text x="65" y="80" textAnchor="middle" style={{ fontSize: 10, fill: 'var(--fg-3)' }}>typy dostaw</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {mix.map((m) => (
          <div className="li" key={m.name} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 7 }}>
            <span className="sw" style={{ background: m.color || 'var(--accent)', width: 10, height: 10, borderRadius: 3 }} /> {m.name}
            <b className="num" style={{ marginLeft: 'auto', paddingLeft: 14 }}>{m.value}%</b>
          </div>
        ))}
      </div>
    </div>
  );
}

function LineChart({ data }: { data: Hourly[] }) {
  const H = data.map((d) => d.time);
  const max = Math.max(...H, 1);
  const W = 460; const HT = 150;
  const pts = H.map((v, i) => `${(i / Math.max(1, H.length - 1)) * W},${HT - (v / max) * (HT - 16)}`).join(' ');
  const area = `0,${HT} ${pts} ${W},${HT}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${HT + 20}`} preserveAspectRatio="none" style={{ overflow: 'visible' }}>
      <defs>
        <linearGradient id="lg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.28" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill="url(#lg)" />
      <polyline points={pts} fill="none" stroke="var(--accent)" strokeWidth="2.5" strokeLinejoin="round" strokeLinecap="round" />
      {H.map((v, i) => (
        <circle key={i} cx={(i / Math.max(1, H.length - 1)) * W} cy={HT - (v / max) * (HT - 16)} r="3" fill="var(--surface)" stroke="var(--accent)" strokeWidth="2" />
      ))}
      {data.map((d, i) => (
        i % Math.ceil(data.length / 6 || 1) === 0
          ? <text key={d.hour} x={(i / Math.max(1, data.length - 1)) * W} y={HT + 16} textAnchor="middle" style={{ fontSize: 10, fill: 'var(--fg-3)' }}>{d.hour}</text>
          : null
      ))}
    </svg>
  );
}

export default function StatsPage() {
  const { data: stats, isLoading } = useQuery({
    queryKey: ['statistics'],
    queryFn: async () => (await axios.get(`${API_URL}/api/orders/stats/detailed`)).data,
    refetchInterval: 60000,
  });

  if (isLoading && !stats) return <PageSkeleton />;

  const dailyData: Daily[] = stats?.dailyData || [];
  const deliveryTypes: DeliveryType[] = stats?.deliveryTypes || [];
  const hourly: Hourly[] = stats?.hourlyEfficiency || [];
  const summary = stats?.summary || {};

  const empty = !dailyData.length && !deliveryTypes.length && !hourly.length;

  return (
    <div className="page">
      <div className="page-head">
        <div><h1>Statystyki</h1><div className="ph-sub">Ostatnie 7 dni · odświeżanie co minutę</div></div>
        <div className="actions"><select className="select" defaultValue="week"><option value="week">Ten tydzień</option></select></div>
      </div>
      <div className="kpi-row" style={{ marginBottom: 16 }}>
        <KpiCard icon="package" label="Zamówienia / tydzień" value={summary.weeklyOrders ?? 0}
          delta={summary.weeklyChange != null ? `${summary.weeklyChange >= 0 ? '+' : ''}${summary.weeklyChange}%` : undefined}
          dir={summary.weeklyChange != null && summary.weeklyChange < 0 ? 'down' : 'up'} />
        <KpiCard icon="timer" label="Śr. czas pakowania" value={summary.avgPackingTime ?? '0:00'} />
        <KpiCard icon="alert-circle" label="Błędy" value={summary.errorCount ?? 0}
          delta={summary.errorRate != null ? `${summary.errorRate}%` : undefined}
          dir={summary.errorRate > 1 ? 'down' : 'up'} />
        <KpiCard icon="gauge" label="% efektywności" value={`${summary.efficiency ?? 0}%`} />
      </div>

      {empty ? (
        <EmptyState icon="bar-chart-3" title="Brak danych statystycznych" desc="Wybierz inny zakres lub poczekaj na pierwsze zamówienia." />
      ) : (
        <>
          <div className="grid" style={{ gridTemplateColumns: '1.4fr 1fr', marginBottom: 16 }}>
            <div className="card"><div className="card-head"><h3>Dzienne zamówienia i paczki</h3></div><div className="card-pad"><BarChart data={dailyData} /></div></div>
            <div className="card"><div className="card-head"><h3>Typy dostaw</h3></div><div className="card-pad"><Donut mix={deliveryTypes} /></div></div>
          </div>
          <div className="card">
            <div className="card-head"><h3>Efektywność godzinowa</h3><span className="ch-sub">śr. czas pakowania / godzinę (min)</span></div>
            <div className="card-pad">
              {hourly.length ? <LineChart data={hourly} /> : <div className="muted" style={{ fontSize: 13 }}><Icon name="info" size={14} /> Brak danych godzinowych.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
